import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";

/**
 * Phase 2D.3B.3 — the dashboard SCOPE read model.
 *
 * One call to ``GET /api/dashboard/scope`` answers everything the scope bar
 * must not guess: which teams this actor may select, whether the team axis is
 * locked (a Manager reads exactly the team it manages), and which team each
 * selectable person belongs to — so a person filter can be re-scoped the moment
 * the team changes instead of keeping a stale selection from another team.
 *
 * Fails soft: a refused or unavailable scope leaves the bar with no selectors
 * rather than blocking the whole dashboard.
 */
const EMPTY_SCOPE = {
  role: null,
  can_filter_team: false,
  can_filter_owner: false,
  team_locked: false,
  locked_team_id: null,
  allow_unassigned_team: false,
  teams: [],
  members: [],
};

export function useDashboardScope(orgId, { enabled = true } = {}) {
  const [scope, setScope] = useState(EMPTY_SCOPE);
  const [loading, setLoading] = useState(Boolean(orgId && enabled));

  const load = useCallback(() => {
    if (!orgId || !enabled) {
      setScope(EMPTY_SCOPE);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    api
      .get("/dashboard/scope", { signal: controller.signal })
      .then((r) => setScope({ ...EMPTY_SCOPE, ...(r.data || {}) }))
      .catch((err) => {
        const aborted =
          err?.name === "CanceledError" ||
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED";
        if (aborted) return;
        reportError("dashboard.loadScope", err);
        setScope(EMPTY_SCOPE);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [orgId, enabled]);

  useEffect(() => load(), [load]);

  return { scope, scopeLoading: loading, reloadScope: load };
}

export { EMPTY_SCOPE };
